import moment from 'moment-timezone';

const TIMELINE_WINDOW_HOURS = 24;

/** Extra time after nominal session end before computing the window (data / clock safety). */
export const SESSION_END_BUFFER_MINUTES = 30;

/**
 * Viewer sessions API may send placeholders (e.g. "-") for ongoing / unknown end.
 * moment rejects those via isValid(). We cannot use isValid() alone: moment(undefined) is valid ("now").
 */
export function isUsableSessionEndTime(sessionEndTime: unknown): boolean {
  return sessionEndTime != null && moment(sessionEndTime).isValid();
}

/**
 * Calculate the timeline interval to render for a session.
 *
 * - When `sessionEndTime` is usable, the window is the last 24 hours of the
 *   session (end + SESSION_END_BUFFER_MINUTES, capped to now; start = end - 24h).
 * - When `sessionEndTime` is missing / unusable, center the 24h window on
 *   `sessionStartTime + 30m`: end = start + 12h30m (capped to now),
 *   start_of_window = end - 24h. This is the historical ±12h behaviour
 *   from CE-6701 — it surfaces context on both sides of the session start.
 */
export function calcInterval({ sessionStartTime, sessionEndTime }) {
  if (isUsableSessionEndTime(sessionEndTime)) {
    const endTime = moment(sessionEndTime).add(SESSION_END_BUFFER_MINUTES, 'minutes');
    const cappedEnd = endTime.isAfter(moment()) ? moment() : moment(endTime);
    const timelineStart = moment(cappedEnd).subtract(TIMELINE_WINDOW_HOURS, 'hours');
    return `${timelineStart.toISOString()}/${cappedEnd.toISOString()}`;
  }

  const midTime = moment(sessionStartTime).add(SESSION_END_BUFFER_MINUTES, 'minutes');
  let end = moment(midTime).add(TIMELINE_WINDOW_HOURS / 2, 'hours');
  if (end.isAfter(moment())) {
    end = moment();
  }
  const start = moment(end).subtract(TIMELINE_WINDOW_HOURS, 'hours');
  return `${start.toISOString()}/${end.toISOString()}`;
}

export function updateRequest(request) {
  const { meta } = request;
  /// Refresh timeline interval for saved dashboards to use last 24h from end time
  if (meta && meta.sessionStartTime) {
    request.timelineInterval = calcInterval({
      sessionStartTime: meta.sessionStartTime,
      sessionEndTime: meta.sessionEndTime,
    });
  }
}
